/**
 * @copyright Copyright 2016 The MathWorks, Inc.
 * @description Enumeration of Buttons and Button types. <br />
 * There are five types of commit buttons, the framework will
 * place the buttons in the Commit Button Area in the following order from left-to-right:
 *  1. [Do It]
 *  2. [Don't Do It]
 *  3. [Cancel]
 *  4. [Apply]
 *  5. [Revert]
 *
 * Only allow one same type of buttons could exist, if several same type of buttons be
 * specified, it will accept the first one. <br />
 *
 * Expand the Members to see enumerations of ButtonEnum and TYPE.
 * @module mw-dialogs/ButtonEnum
 */
define(["dojo/i18n!./l10n/nls/dialog"], function (dialogL10n) {

    /**
     * @name TYPE
     * @description Enum of button types
     * @readonly
     * @static
     * @property {string} HELP - Help button type
     * @property {string} SPL1 - Spl1 button type
     * @property {string} SPL2 - Spl2 button type
     * @property {string} DO_IT - DoIt button type
     * @property {string} DONT_DO_IT - DontDoIt button type
     * @property {string} CANCEL - Cancel button type
     * @property {string} APPLY - Apply button type
     * @property {string} REVERT - Revert button type
     * @example ButtonEnum.TYPE.Help
     */
    const buttonType = Object.freeze({
        // Supplemental Button Types
        HELP: "Help",
        SPL1: "Spl1",
        SPL2: "Spl2",
        // Action Button Types
        DO_IT: "DoIt",
        DONT_DO_IT: "DontDoIt",
        CANCEL: "Cancel",
        APPLY: "Apply",
        REVERT: "Revert"
    });

    /**
     * @member ButtonEnum
     * @description Enum of buttons
     * @readonly
     * @static
     * @property {string} NO_DEFAULT_ACTION - if the developer does not want a default action
     * for dialog
     * @property {enum} TYPE - button type
     * @property {object} HELP - Help button
     * @property {object} OK - Ok button
     * @property {object} YES - Yes button
     * @property {object} SUBMIT - Submit button
     * @property {object} SAVE - Save button
     * @property {object} FIND - Find button
     * @property {object} CREATE - Create button
     * @property {object} DONT_SAVE - Don't Save button
     * @property {object} NO - No button
     * @property {object} CANCEL - Cancel button
     * @property {object} CLOSE - Close button
     * @property {object} APPLY - Apply button
     * @property {object} REVERT - Revert button
     * @example ButtonEnum.OK
     */
    const buttonEnum = {
        NO_DEFAULT_ACTION: "NoDefaultAction",
        // Button Types:
        TYPE: buttonType,
        // Predefined Buttons:
        // Help Button
        HELP: Object.freeze({
            type: buttonType.HELP,
            label: dialogL10n.help,
            "data-test-id": "HelpButton"
        }),
        // Do It Buttons
        OK: Object.freeze({
            type: buttonType.DO_IT,
            label: dialogL10n.ok,
            "data-test-id": "OkButton"
        }),
        YES: Object.freeze({
            type: buttonType.DO_IT,
            label: dialogL10n.yes,
            "data-test-id": "YesButton"
        }),
        SUBMIT: Object.freeze({
            type: buttonType.DO_IT,
            label: dialogL10n.submit,
            "data-test-id": "SubmitButton"
        }),
        SAVE: Object.freeze({
            type: buttonType.DO_IT,
            label: dialogL10n.save,
            "data-test-id": "SaveButton"
        }),
        FIND: Object.freeze({
            type: buttonType.DO_IT,
            label: dialogL10n.find,
            "data-test-id": "FindButton"
        }),
        CREATE: Object.freeze({
            type: buttonType.DO_IT,
            label: dialogL10n.create,
            "data-test-id": "CreateButton"
        }),
        // Don't Do It Buttons
        DONT_SAVE: Object.freeze({
            type: buttonType.DONT_DO_IT,
            label: dialogL10n.dontSave,
            "data-test-id": "DontSaveButton"
        }),
        NO: Object.freeze({
            type: buttonType.DONT_DO_IT,
            label: dialogL10n.no,
            "data-test-id": "NoButton"
        }),
        // Cancel Buttons
        CANCEL: Object.freeze({
            type: buttonType.CANCEL,
            label: dialogL10n.cancel,
            "data-test-id": "CancelButton"
        }),
        CLOSE: Object.freeze({
            type: buttonType.CANCEL,
            label: dialogL10n.close,
            "data-test-id": "CloseButton"
        }),
        // Apply Buttons
        APPLY: Object.freeze({
            type: buttonType.APPLY,
            label: dialogL10n.apply,
            "data-test-id": "ApplyButton"
        }),
        // Revert Buttons
        REVERT: Object.freeze({
            type: buttonType.REVERT,
            label: dialogL10n.revert,
            "data-test-id": "RevertButton"
        })
    };

    Object.freeze(buttonEnum);

    return buttonEnum;
});
